import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';

// Key in localStorage where we cache the email when "Zapamti me" is checked.
// The password is NEVER stored — only the email is, so the field auto-fills
// on the next visit. Browsers may still offer to save the password
// independently in their built-in password manager, but our app does not.
const REMEMBER_KEY = 'remember_email';
const AUTOLOGOUT_FLAG = 'auto_logout_triggered';

export default function LoginPage({ setIsAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // On mount: if a previous session left a "remembered" email AND the user
  // wasn't auto-logged-out (which clears it), pre-fill the email field.
  useEffect(() => {
    if (localStorage.getItem(AUTOLOGOUT_FLAG) === '1') {
      // Auto-logout happened → wipe any cached email so this device doesn't
      // expose the previous user. Then clear the flag.
      localStorage.removeItem(REMEMBER_KEY);
      localStorage.removeItem(AUTOLOGOUT_FLAG);
    } else {
      const cached = localStorage.getItem(REMEMBER_KEY);
      if (cached) {
        setEmail(cached);
        setRemember(true);
      }
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Persist the email ONLY when "Zapamti me" is checked.
      if (remember) {
        localStorage.setItem(REMEMBER_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }

      setIsAuthenticated(true);
      toast.success('Uspješno ste se prijavili!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri prijavi');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md shadow-md" data-testid="login-card">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-56 h-24 flex items-center justify-center" data-testid="login-logo">
            <img
              src="/login-logo.png"
              alt="CRM Finance Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <CardDescription className="text-sm text-slate-600">
            Client Relationship Management
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form" autoComplete="on">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold tracking-wider uppercase text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white border-slate-300 text-sm"
                data-testid="login-email-input"
                placeholder="vas.email@primjer.com"
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold tracking-wider uppercase text-slate-500">
                Lozinka
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-white border-slate-300 text-sm"
                data-testid="login-password-input"
                placeholder="••••••••"
                name="password"
                autoComplete="current-password"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="remember-me-row">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                data-testid="remember-me-checkbox"
              />
              Zapamti me na ovom uređaju
              {remember && (
                <span className="ml-1 text-xs text-blue-600 font-medium" data-testid="remember-me-hint">
                  ✓ Email i lozinka će biti zapamćeni
                </span>
              )}
            </label>

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabled={isLoading}
              data-testid="login-submit-button"
            >
              {isLoading ? 'Prijava...' : 'Prijavite se'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
